import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';

export interface CommandHistoryCreate {
  user_id: string;
  command: string;
  args?: string[];
  result?: Record<string, any>;
  execution_time?: number;
  success: boolean;
  error_message?: string;
  thread_model_context?: Record<string, any>;
}

export interface CommandHistoryResponse {
  id: string;
  user_id: string;
  command: string;
  args?: string[];
  result?: Record<string, any>;
  execution_time?: number;
  success: boolean;
  error_message?: string;
  timestamp: string;
  thread_model_context?: Record<string, any>;
}

export class CommandHistoryService extends BaseService {
  /**
   * Save command history to backend
   */
  static async saveCommandHistory(historyData: CommandHistoryCreate): Promise<CommandHistoryResponse> {
    // Use /commands/history endpoint (documented) instead of /commands/command-history
    return this.post<CommandHistoryResponse>('/commands/history', historyData as unknown as Record<string, unknown>);
  }

  /**
   * Get command history for a user
   * GET /commands/history?user_id={userId}
   */
  static async getCommandHistory(
    userId: string, 
    command?: string, 
    success?: boolean, 
    skip = 0, 
    limit = 100
  ): Promise<CommandHistoryResponse[]> {
    try {
      const params = new URLSearchParams();
      params.append('user_id', userId);
      if (command) params.append('command', command);
      if (success !== undefined) params.append('success', success.toString());
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      
      // Use /commands/history endpoint (documented)
      const response = await this.get<any>(`/commands/history?${params.toString()}`);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      } else if (response && typeof response === 'object') {
        // Check for wrapped responses
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && typeof response.data === 'object') {
          // Backend returns: {success: true, data: {...}} or {success: true, data: [...]}
          // Check if data itself is an array first
          if (Array.isArray(response.data)) {
            return response.data;
          }
          // PRIORITY: Check for commands array first (most common format)
          if (Array.isArray(response.data.commands)) {
            // Backend returns: {success: true, data: {commands: [...], count, limit, offset}}
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ [CommandHistoryService.getCommandHistory] Found array in data.commands:`, {
                count: response.data.commands.length,
                totalCount: response.data.count,
                limit: response.data.limit,
                offset: response.data.offset
              });
            }
            return response.data.commands;
          }
          // Then check for other nested array structures
          if (Array.isArray(response.data.command_history)) {
            return response.data.command_history;
          } else if (Array.isArray(response.data.history)) {
            return response.data.history;
          } else if (Array.isArray(response.data.data)) {
            // Nested data.data structure
            return response.data.data;
          } else if (Array.isArray(response.data.items)) {
            return response.data.items;
          } else if (Array.isArray(response.data.results)) {
            return response.data.results;
          }
          // If data is an object but not an array, check if it has a single item or different structure
          // Some backends return {success: true, data: {command_history: [...]}}
          if (response.success && response.data) {
            // Try common array field names
            for (const key of ['commands', 'items', 'results', 'entries', 'records', 'list']) {
              if (Array.isArray(response.data[key])) {
                if (process.env.NODE_ENV === 'development') {
                  console.log(`✅ [CommandHistoryService.getCommandHistory] Found array in data.${key}`);
                }
                return response.data[key];
              }
            }
          }
        } else if (Array.isArray(response.command_history)) {
          return response.command_history;
        } else if (Array.isArray(response.history)) {
          return response.history;
        }
      }
      
      // If response is not an array, return empty array
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ [CommandHistoryService.getCommandHistory] Unexpected response format:', {
          type: typeof response,
          isArray: Array.isArray(response),
          response,
          responseString: JSON.stringify(response, null, 2)
        });
      }
      return [];
    } catch (error: any) {
      // Handle 404 as empty history (user might not have any command history yet)
      if (error.response?.status === 404) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📭 [CommandHistoryService.getCommandHistory] No command history found for user:', userId);
        }
        return [];
      }
      
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error('❌ [CommandHistoryService.getCommandHistory] Error fetching command history:', {
          userId,
          error: error.message,
          status: error.response?.status
        });
      }
      return [];
    }
  }

  /**
   * Get command history for a specific thread
   * Note: Backend doesn't have direct thread_id filter, so we filter client-side using thread_model_context
   */
  static async getCommandHistoryByThread(
    threadId: string,
    userId: string,
    options?: {
      command?: string;
      success?: boolean;
      skip?: number;
      limit?: number;
    }
  ): Promise<CommandHistoryResponse[]> {
    try {
      // Get all command history for user
      const allHistory = await this.getCommandHistory(
        userId,
        options?.command,
        options?.success,
        options?.skip || 0,
        options?.limit || 100
      );
      
      // Ensure allHistory is an array
      if (!Array.isArray(allHistory)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [CommandHistoryService.getCommandHistoryByThread] getCommandHistory did not return an array:', {
            type: typeof allHistory,
            value: allHistory
          });
        }
        return [];
      }
      
      // Filter by thread_id in thread_model_context
      return allHistory.filter(entry => {
        if (!entry.thread_model_context) return false;
        
        // Check if thread_model_context contains the thread_id
        // thread_model_context is typed as Record<string, any>, so it's always an object
        const context = entry.thread_model_context;
        
        // Check for thread_id in the context object
        return context.thread_id === threadId || 
               context.threadId === threadId;
      });
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [CommandHistoryService.getCommandHistoryByThread] Error loading command history:', {
          threadId,
          userId,
          error: error.message
        });
      }
      return [];
    }
  }
}
